import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeliverableImportTypes } from '@mono/proto-clients';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { PROGRAM_LEVEL_KEY, workstreamLabel } from './shared';
const { DeliverableImportAction, DeliverableImportDecision } = DeliverableImportTypes;
const UNRESOLVED_PREVIEW_COUNT = 5;
/**
 * OVERVIEW body: server totals for the staged import plus a per-workstream
 * table. Renders only from `review` (GetDeliverableImportReview) — never
 * from the card payload — per the proto's DeliverableImportCard doc comment.
 * Non-blocking (no buttons): the proto omits `_requiresApproval` for this
 * kind.
 */ export function OverviewCard({ review }) {
    const [showAllUnresolved, setShowAllUnresolved] = useState(false);
    const workstreamsFound = review.workstreams.filter((ws)=>ws.key !== PROGRAM_LEVEL_KEY).length;
    const totals = review.totals;
    const unresolved = review.proposals.filter((p)=>p.action === DeliverableImportAction.NEEDS_CLARIFICATION && p.decision !== DeliverableImportDecision.SUPERSEDED);
    const visibleUnresolved = showAllUnresolved ? unresolved : unresolved.slice(0, UNRESOLVED_PREVIEW_COUNT);
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-2 space-y-3 text-sm text-gray-700",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium text-gray-900",
                        children: review.fileName
                    }),
                    review.sheetName && /*#__PURE__*/ _jsxs("span", {
                        className: "text-gray-500",
                        children: [
                            ' · sheet "',
                            review.sheetName,
                            '"'
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-xs text-gray-600",
                children: [
                    "Found ",
                    workstreamsFound,
                    " workstream",
                    workstreamsFound === 1 ? '' : 's',
                    " —",
                    ' ',
                    totals?.recurringRecords ?? 0,
                    " recurring, ",
                    totals?.oneTimeRecords ?? 0,
                    " one-time,",
                    ' ',
                    totals?.asNeededRecords ?? 0,
                    " as-needed."
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-3 gap-2 text-xs",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border rounded-md p-2 bg-gray-50",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: "Workbook rows"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-base font-semibold text-gray-900",
                                children: totals?.workbookRows ?? 0
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border rounded-md p-2 bg-gray-50",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: "Deliverable records"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-base font-semibold text-gray-900",
                                children: totals?.deliverableRecords ?? 0
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border rounded-md p-2 bg-gray-50",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: "Scheduled occurrences"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-base font-semibold text-gray-900",
                                children: totals?.scheduledOccurrences ?? 0
                            })
                        ]
                    })
                ]
            }),
            unresolved.length > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-xs font-medium text-gray-600 mb-1",
                        children: [
                            unresolved.length,
                            " proposal",
                            unresolved.length === 1 ? '' : 's',
                            " need",
                            unresolved.length === 1 ? 's' : '',
                            " clarification"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1",
                        children: visibleUnresolved.map((p)=>/*#__PURE__*/ _jsxs("li", {
                                className: "text-xs border-l-2 border-amber-300 pl-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-gray-500",
                                        children: [
                                            "Row",
                                            (p.source?.rows.length ?? 0) === 1 ? '' : 's',
                                            ' ',
                                            p.source?.rows.join(', ') || '—',
                                            ":"
                                        ]
                                    }),
                                    ' ',
                                    p.clarificationQuestion
                                ]
                            }, p.id))
                    }),
                    unresolved.length > UNRESOLVED_PREVIEW_COUNT && /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setShowAllUnresolved((s)=>!s),
                        className: "text-xs font-medium text-blue-600 hover:text-blue-700 mt-1",
                        children: showAllUnresolved ? 'Show less' : `Show all ${unresolved.length}`
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-auto max-h-64 border rounded-md",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full text-xs",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "sticky top-0 bg-gray-50 text-gray-500",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left font-medium px-2 py-1",
                                        children: "Workstream"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Proposed Creates"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Proposed Updates"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Already Matches"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Needs Clarification"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: review.workstreams.map((ws)=>/*#__PURE__*/ _jsxs("tr", {
                                    "data-testid": Testids.deliverableImport.workstreamRow,
                                    className: "border-t",
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-gray-900",
                                            children: workstreamLabel(ws)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: ws.proposedCreates
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: ws.proposedUpdates
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: ws.alreadyMatches
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: ws.needsClarification
                                        })
                                    ]
                                }, ws.key))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "text-xs italic text-gray-500",
                children: "I have not changed the program yet."
            })
        ]
    });
}
